import { hiddenCategories } from 'components/marketingSite/blog/utils/blogUtils';
import { SUCCESS_STORIES_BLOG_CATEGORY_ID } from 'components/marketingSite/successStories/constants';
import PostComponent from 'components/marketingSite/successStories/post/PostComponent';
import HeadMetadataLangAlternate from 'components/shared/HeadMetadataLangAlternate';
import { SlugsContext } from 'context/slugs/slugs.context';
import { useI18n } from 'hooks/useI18n';
import parse from 'html-react-parser';
import { Multilingual } from 'interfaces/offer.interface';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Script from 'next/script';
import { useContext, useEffect } from 'react';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { config } from 'utils/config';
import { avoidRateLimit } from 'utils/rate-limit';
import {
  WP_REST_API_Categories,
  WP_REST_API_Post,
  WP_REST_API_Tags
} from 'wp-types';

export type WP_NextPrev = {
  id: number;
  slug: string;
  title: string;
};

type PostPagerProps = {
  disqusShortname?: string;
  post: WP_REST_API_Post & {
    next: WP_NextPrev | null;
    previous: WP_NextPrev | null;
  };
  categories: WP_REST_API_Categories;
  relatedPosts: WP_REST_API_Post[];
  tags: WP_REST_API_Tags;
};

const PostPage: React.FC<PostPagerProps> = (props) => {
  const { t } = useI18n('success-stories');
  const yoastHead = parse(`${props.post.yoast_head}`);
  const { setSlug, clearSlug } = useContext(SlugsContext);

  useEffect(() => {
    setSlug(props.post.language_slugs as Multilingual);

    return () => {
      clearSlug();
    };
  }, [props.post.id]);

  return (
    <>
      <Head>{yoastHead}</Head>
      <Script
        id="successStoriesMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />
      <HeadMetadataLangAlternate
        url={APP_URLS.successStories}
        path={{
          ES: `${props.post.slug}`,
          EN: `${props.post.slug}`
        }}
      />
      <PostComponent {...props} />
    </>
  );
};

export default PostPage;

export async function getStaticPaths() {
  try {
    await avoidRateLimit();
    const slugsES = await WPService.getSlugs('es');
    const slugsEN = await WPService.getSlugs('en');

    const pathsES = slugsES
      .slice(0, +config.pregenerateBlogPosts)
      .map((slug) => ({
        params: { slug },
        locale: 'es'
      }));

    const pathsEN = slugsEN
      .slice(0, +config.pregenerateBlogPosts)
      .map((slug) => ({
        params: { slug },
        locale: 'en'
      }));

    return {
      paths: [...pathsES, ...pathsEN],
      fallback: 'blocking'
    };
  } catch (error) {
    console.error('An error occurred during getStaticPaths:', error);

    return {
      paths: [],
      fallback: 'blocking'
    };
  }
}

export async function getStaticProps({
  locale,
  params
}: {
  params: {
    slug: string;
  };
  locale: string;
}) {
  await avoidRateLimit();
  let post = await WPService.getPost(params.slug, locale);

  await avoidRateLimit();
  const relatedPosts = await WPService.getPosts({
    page: undefined,
    category: SUCCESS_STORIES_BLOG_CATEGORY_ID,
    exclude: String(post?.id),
    locale: locale || 'en',
    perPage: 3
  });
  await avoidRateLimit();
  const categories = (await WPService.getAllCategories(locale || 'en')).filter(
    (c) =>
      hiddenCategories.includes(c.slug as typeof hiddenCategories[number]) ===
      false
  );

  await avoidRateLimit();
  const tagsInfo = await WPService.getTagsFromPosts({
    posts: [post, ...(relatedPosts?.data || [])],
    locale: locale || 'en'
  });

  if (!post) {
    console.log('No post found, trying other language');
  }
  if (!post) {
    post = await WPService.getPost(params.slug, locale === 'en' ? 'es' : 'en');
  }

  if (!post) {
    return {
      revalidate: 10,
      redirect: {
        destination: `/${locale}/404`,
        permanent: false
      }
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'topbar',
        'success-stories',
        'footer'
      ])),
      post,
      disqusShortname: config.disqusShortname,
      relatedPosts: relatedPosts?.data || [],
      categories: categories || [],
      tags: tagsInfo || []
    },
    revalidate: 10
  };
}
